import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

/**
 * A representative delivery address per selling country. Rates are set per
 * shipping zone (country level), so any valid address in the country returns
 * the same price — but Shopify won't return rates at all without a postal
 * code, and some countries also need a province.
 *
 * When a new country is added to a market, add it here; until then it shows
 * the "enter your full address at checkout" message.
 *
 * @type {Record<string, {zip?: string, provinceCode?: string}>}
 */
const REPRESENTATIVE_ADDRESSES = {
  AT: { zip: '1010' },
  AU: { zip: '2000', provinceCode: 'NSW' },
  BE: { zip: '1000' },
  BG: { zip: '1000' },
  CA: { zip: 'M5V 3L9', provinceCode: 'ON' },
  CH: { zip: '8001' },
  CN: { zip: '100000', provinceCode: 'BJ' },
  CY: { zip: '1010' },
  CZ: { zip: '110 00' },
  DK: { zip: '1050' },
  EE: { zip: '10111' },
  ES: { zip: '28001', provinceCode: 'M' },
  FI: { zip: '00100' },
  FR: { zip: '75001' },
  GR: { zip: '105 57' },
  HK: { provinceCode: 'HK' },
  HR: { zip: '10000' },
  HU: { zip: '1051' },
  IE: { zip: 'D02 X285', provinceCode: 'D' },
  IT: { zip: '00118', provinceCode: 'RM' },
  JP: { zip: '100-0001', provinceCode: 'JP-13' },
  LT: { zip: '01100' },
  LU: { zip: '1111' },
  LV: { zip: 'LV-1050' },
  MT: { zip: 'VLT 1010' },
  MY: { zip: '50000', provinceCode: 'KUL' },
  NL: { zip: '1012 AB' },
  PL: { zip: '00-001' },
  PT: { zip: '1100-148' },
  RO: { zip: '010011', provinceCode: 'B' },
  SE: { zip: '111 22' },
  SG: { zip: '018956' },
  SI: { zip: '1000' },
  SK: { zip: '811 01' },
  TW: { zip: '100' },
  US: { zip: '10001', provinceCode: 'NY' },
};

const ESTIMATE_MUTATION = `#graphql
  mutation ShippingEstimate(
    $lines: [CartLineInput!]!
    $country: CountryCode!
    $address: CartDeliveryAddressInput!
  ) {
    cartCreate(
      input: {
        lines: $lines
        buyerIdentity: { countryCode: $country }
        delivery: { addresses: [{ selected: true, address: { deliveryAddress: $address } }] }
      }
    ) {
      cart {
        deliveryGroups(first: 10) {
          nodes {
            deliveryOptions {
              estimatedCost { amount currencyCode }
            }
          }
        }
      }
      userErrors { message }
    }
  }`;

/**
 * Shows a live shipping-cost estimate sourced directly from the store's
 * configured shipping rates — never a rate table duplicated in the theme —
 * so the number shown here can't drift from what checkout actually charges.
 *
 * Both modes price a throwaway Storefront API cart, so the shopper's real
 * cart is never touched. Set by the `data-context` attribute (see
 * `snippets/shipping-estimate.liquid`):
 * - "cart": the shopper's current cart.
 * - "product": the current cart plus one of this variant.
 *
 * @typedef {Object} ShippingEstimateRefs
 * @property {HTMLElement} message
 * @property {HTMLSelectElement} [countrySelect]
 * @property {HTMLTemplateElement} [textEstimate]
 * @property {HTMLTemplateElement} [textFallback]
 * @property {HTMLTemplateElement} [textNeedsAddress]
 *
 * @typedef {{ok: boolean, amount?: number, currency?: string, needsAddress?: boolean}} EstimateResult
 *
 * @extends {Component<ShippingEstimateRefs>}
 */
class ShippingEstimateComponent extends Component {
  requiredRefs = ['message'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** The message text this component last rendered, to tell its own writes apart from a re-render's. */
  #renderedText = '';

  /**
   * When the cart drawer or product block is re-rendered (Section Rendering
   * API + morph), the message is reset to the server's "Calculating…" text
   * and a new variant id may arrive. morph doesn't reliably call
   * `updatedCallback` on this element, so watch for that reset directly.
   */
  #morphObserver = new MutationObserver((mutations) => {
    const variantChanged = mutations.some((m) => m.type === 'attributes' && m.attributeName === 'data-variant-id');
    const { message } = this.refs;
    const textReset = message instanceof HTMLElement && message.textContent !== this.#renderedText;

    if (variantChanged || (textReset && !this.#activeFetch)) this.#refresh();
  });

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#morphObserver.observe(this, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['data-variant-id'],
    });

    // Only fetch once the estimate is actually visible — it's often below
    // the fold (cart drawer summary, product page) and the first paint
    // shouldn't wait on it.
    this.#observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.#observer?.disconnect();
      this.#observer = null;
      this.#refresh();
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#morphObserver.disconnect();
    this.#observer?.disconnect();
    this.#activeFetch?.abort();
    this.#activeFetch = null;
  }

  /** Invoked via `on:change` from the country `<select>`. */
  handleCountryChange() {
    const value = this.refs.countrySelect instanceof HTMLSelectElement ? this.refs.countrySelect.value : '';
    if (!value) return;
    this.dataset.country = value;
    this.#refresh();
  }

  #handleCartLinesUpdate = () => this.#refresh();

  async #refresh() {
    const country = this.dataset.country;
    if (!country) return;

    // Cancel a still-in-flight request from a previous country/variant/cart
    // so a slow response can't clobber a newer one.
    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;
    this.setAttribute('aria-busy', 'true');

    /** @type {EstimateResult} */
    let result;
    try {
      result = await this.#estimate(country, controller.signal);
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error('[shipping-estimate]', error);
      result = { ok: false };
    }

    if (controller.signal.aborted) return;
    this.#activeFetch = null;
    this.removeAttribute('aria-busy');
    this.#render(result);
  }

  /**
   * @param {string} country
   * @param {AbortSignal} signal
   * @returns {Promise<EstimateResult>}
   */
  async #estimate(country, signal) {
    const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { signal });
    const currentCart = await cartResponse.json().catch(() => ({ items: [] }));

    const lines = (Array.isArray(currentCart.items) ? currentCart.items : []).map((item) => ({
      merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
      quantity: item.quantity,
    }));
    if (this.dataset.context === 'product' && this.dataset.variantId) {
      lines.push({ merchandiseId: `gid://shopify/ProductVariant/${this.dataset.variantId}`, quantity: 1 });
    }
    if (lines.length === 0) return { ok: false };

    // Keyed on the exact lines + country, so a cached price can never be
    // shown for a cart that has since changed.
    const cacheKey = `shipping-estimate:${country}:${JSON.stringify(lines)}`;
    const cached = this.#readCache(cacheKey);
    if (cached) return cached;

    const result = await this.#fetchEstimate(lines, country, signal);
    if (result.ok) this.#writeCache(cacheKey, result);
    return result;
  }

  /**
   * @param {{merchandiseId: string, quantity: number}[]} lines
   * @param {string} country
   * @param {AbortSignal} signal
   * @returns {Promise<EstimateResult>}
   */
  async #fetchEstimate(lines, country, signal) {
    const endpoint = this.dataset.storefrontEndpoint;
    if (!endpoint) return { ok: false };

    /** @type {Record<string, string>} */
    const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    // Optional — the Storefront API also answers these queries without a
    // token; a token only raises the rate limit.
    if (this.dataset.storefrontToken) headers['X-Shopify-Storefront-Access-Token'] = this.dataset.storefrontToken;

    const address = { countryCode: country, ...REPRESENTATIVE_ADDRESSES[country] };

    const response = await fetch(endpoint, {
      method: 'POST',
      signal,
      headers,
      body: JSON.stringify({ query: ESTIMATE_MUTATION, variables: { lines, country, address } }),
    });

    const payload = await response.json().catch(() => null);
    const cart = payload?.data?.cartCreate?.cart;
    const userErrors = payload?.data?.cartCreate?.userErrors;

    if (!response.ok || payload?.errors?.length || userErrors?.length || !cart) {
      console.error('[shipping-estimate]', payload?.errors ?? userErrors ?? response.status);
      return { ok: false };
    }

    const groups = cart.deliveryGroups?.nodes ?? [];
    if (groups.length === 0) return { ok: false, needsAddress: true };

    // Each group is a shipment Shopify would split the order into (currently
    // always one, since every product shares a single shipping profile) —
    // sum the cheapest option per group for the order's total.
    let total = 0;
    let currency = '';
    for (const group of groups) {
      const options = group.deliveryOptions ?? [];
      if (options.length === 0) return { ok: false, needsAddress: true };

      const cheapest = options.reduce((min, option) =>
        Number(option.estimatedCost.amount) < Number(min.estimatedCost.amount) ? option : min
      );
      total += Number(cheapest.estimatedCost.amount);
      currency = cheapest.estimatedCost.currencyCode;
    }

    return { ok: true, amount: total, currency };
  }

  /**
   * @param {EstimateResult} result
   */
  #render(result) {
    const { message, countrySelect } = this.refs;
    if (!(message instanceof HTMLElement)) return;

    if (result.ok && result.amount != null && result.currency) {
      const minorUnits = convertMoneyToMinorUnits(String(result.amount), result.currency);
      const amountText =
        minorUnits == null ? '' : formatMoney(minorUnits, this.dataset.moneyFormat || '{{amount}}', result.currency);
      const countryText =
        countrySelect instanceof HTMLSelectElement ? (countrySelect.selectedOptions[0]?.textContent ?? '') : '';

      message.textContent = this.#text('textEstimate')
        .replace('__COUNTRY__', countryText.trim())
        .replace('__AMOUNT__', amountText);
      this.removeAttribute('data-state');
    } else {
      message.textContent = this.#text(result.needsAddress ? 'textNeedsAddress' : 'textFallback');
      this.setAttribute('data-state', 'fallback');
    }

    this.#renderedText = message.textContent;
  }

  /** @param {'textEstimate' | 'textFallback' | 'textNeedsAddress'} ref */
  #text(ref) {
    const template = this.refs[ref];
    // A <template>'s children live in `.content` (a DocumentFragment), not
    // as normal child nodes of the element — `template.textContent` is
    // always empty. `.content.textContent` is what actually holds the text.
    return template instanceof HTMLTemplateElement ? (template.content.textContent ?? '').trim() : '';
  }

  /** @param {string} key */
  #readCache(key) {
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  /**
   * @param {string} key
   * @param {object} value
   */
  #writeCache(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Private browsing / storage disabled — estimate still renders, just refetches next time.
    }
  }
}

customElements.define('shipping-estimate-component', ShippingEstimateComponent);
